import React, { useRef, useEffect, useState } from 'react';
import videojs from 'video.js';
import 'videojs-youtube';
import 'video.js/dist/video-js.css';
import VideoProgressMapa from './VideoProgressBar';
import { formatTime } from 'utils/commonUtils';
import { createVideoHeartbeat } from 'utils/video-heartbeat.util';
import { MdPlayArrow, MdPause, MdReplay, MdSkipNext } from 'react-icons/md';

const safeLocalStorage = () => {
  try {
    return window.localStorage;
  } catch (_) {
    return null;
  }
};

// Từ 16/10/2026 mapa do SERVER đóng dấu (giờ server, giới hạn nhịp theo thời
// gian thật) — FE chỉ ghi nhận các ô 15s đã đi qua rồi báo lên qua
// `onHeartbeat`, hiển thị theo mapa/percent server trả về. Trước đây FE tự
// đóng dấu Date.now() của máy và tự tính percent → gửi percent=100 là đủ giờ.
const VideoPlayer = ({
  url,
  videoView,
  onHeartbeat,
  intervalTime = 15,
  logoUrl,
}) => {
  const [mapa, setMapa] = useState(videoView?.mapa || []);
  const [serverPercent, setServerPercent] = useState(Number(videoView?.percent) || 0);
  const [isPlaying, setIsPlaying] = useState(false);
  // Bài học chỉ có tiếng (videoWidth=0 sau loadedmetadata) — hiển thị
  // logo trung tâm trên nền trắng để fill khoảng đen của <video>.
  const [isAudioOnly, setIsAudioOnly] = useState(false);
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const mapaRef = useRef(videoView?.mapa || []);
  const onHeartbeatRef = useRef(onHeartbeat);
  onHeartbeatRef.current = onHeartbeat;
  const mountedRef = useRef(true);

  // 1 bộ điều khiển / lần mount (trang gắn key theo video nên đổi video là
  // mount mới). Logic gửi/gửi lại/lưu ô chờ ở utils/video-heartbeat.util.js.
  const heartbeatRef = useRef(null);
  if (!heartbeatRef.current) {
    heartbeatRef.current = createVideoHeartbeat({
      send: (payload) => (typeof onHeartbeatRef.current === 'function'
        ? onHeartbeatRef.current(payload)
        : Promise.resolve(null)),
      getPosition: () => {
        const p = playerRef.current;
        if (!p) return null;
        return { currentTime: p.currentTime(), duration: p.duration(), paused: p.paused() };
      },
      onResult: (result) => {
        mapaRef.current = result.mapa;
        if (!mountedRef.current) return;
        setMapa(result.mapa);
        setServerPercent(Number(result.percent) || 0);
      },
      storage: safeLocalStorage(),
      storageKey: videoView?.id ? `video-pending:${videoView.id}` : null,
      initialMapa: videoView?.mapa || [],
      intervalSec: intervalTime,
    });
  }

  useEffect(() => {
    if (!url || !videoRef.current || playerRef.current) return;

    const player = videojs(videoRef.current, {
      techOrder: ['html5', 'youtube'],
      sources: [
        {
          src: url,
          type:
            url.includes('youtube.com') || url.includes('youtu.be')
              ? 'video/youtube'
              : 'video/mp4',
        },
      ],
      controls: true,
      responsive: true,
      fluid: true,
      loop: false,
    });

    playerRef.current = player;

    player.ready(() => {
      const seekToLastPosition = () => {
        const startTime = videoView?.currenttime - intervalTime || 0;
        if (startTime > 0) {
          player.currentTime(startTime);
        }
      };

      if (player.duration() > 0) {
        seekToLastPosition();
      } else {
        player.one('loadedmetadata', seekToLastPosition);
        player.one('playing', seekToLastPosition);
      }
    });

    player.on('play', () => setIsPlaying(true));
    player.on('pause', () => setIsPlaying(false));
    player.on('ended', () => setIsPlaying(false));

    // Audio-only detection: HTML5 <video> sau khi load metadata sẽ có
    // videoWidth/videoHeight = 0 nếu source chỉ có audio track. Cờ này
    // bật overlay logo phủ lên khoảng đen (UX feel: file thiếu, không
    // phải video bị lỗi).
    const detectAudioOnly = () => {
      const tech = player.tech_;
      const el = tech?.el?.();
      if (!el) return;
      // Element có thể chưa fire metadata — schedule check
      if (el.readyState >= 1) {
        setIsAudioOnly(el.videoWidth === 0 && el.videoHeight === 0);
      } else {
        const onMeta = () => {
          setIsAudioOnly(el.videoWidth === 0 && el.videoHeight === 0);
          el.removeEventListener('loadedmetadata', onMeta);
        };
        el.addEventListener('loadedmetadata', onMeta);
      }
    };
    player.ready(detectAudioOnly);
    player.on('loadedmetadata', detectAudioOnly);

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [url]);

  useEffect(() => {
    const intervalId = setInterval(() => { heartbeatRef.current.beat().catch(() => {}); }, intervalTime * 1000);
    return () => clearInterval(intervalId);
  }, [intervalTime]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    const hb = heartbeatRef.current;

    const handleTimeUpdate = () => {
      const currentTime = player.currentTime();
      const duration = player.duration();

      if (!videoView) return;
      if (currentTime >= duration) {
        player.pause();
        return;
      }
      if (player.paused()) return;
      hb.touch(currentTime, duration);
    };
    // Dừng (kể cả tự dừng lúc hết video) → gửi lại ô chờ mỗi 15s, tối đa 3 lần.
    const handlePause = () => hb.onPause();
    const handlePlay = () => hb.onPlay();

    player.on('timeupdate', handleTimeUpdate);
    player.on('pause', handlePause);
    player.on('play', handlePlay);
    return () => {
      try {
        player.off('timeupdate', handleTimeUpdate);
        player.off('pause', handlePause);
        player.off('play', handlePlay);
      } catch (_) { /* player đã dispose */ }
    };
  }, []);

  // Rời trang: gửi bù 1 lượt + lưu ô chờ cho lần mở lại video này.
  useEffect(() => () => {
    mountedRef.current = false;
    heartbeatRef.current.dispose().catch(() => {});
  }, []);

  useEffect(() => {
    if (videoView?.mapa) {
      setMapa(videoView.mapa);
      mapaRef.current = videoView.mapa;
      heartbeatRef.current.setMapa(videoView.mapa);
    }
    setServerPercent(Number(videoView?.percent) || 0);
  }, [videoView]);

  const seekTo = (seconds) => {
    const player = playerRef.current;
    if (!player) return;
    if (seconds < 0) seconds = 0;
    player.currentTime(seconds);
    if (player.paused()) {
      player.play();
    }
  };

  const goToNearestUnwatched = () => {
    const firstUnwatchedIndex = mapaRef.current.length > 0
      ? mapaRef.current.findIndex((v) => !(Number(v) > 0))
      : 0;
    if (firstUnwatchedIndex >= 0) {
      const targetTime = firstUnwatchedIndex * intervalTime - intervalTime;
      seekTo(targetTime);
    }
  };

  // Lần đầu xem server chưa có mapa — hiện thanh rỗng theo độ dài player
  // cho tới khi heartbeat đầu tiên trả mapa thật về.
  const displayMapa = mapa.length > 0
    ? mapa
    : new Array(Math.max(0, Math.floor((playerRef.current?.duration() || 0) / intervalTime))).fill(0);
  const watchedCount = displayMapa.filter((v) => Number(v) > 0).length;
  const hasUnwatched = displayMapa.some((v) => !(Number(v) > 0));
  const isCompleted = displayMapa.length > 0 && !hasUnwatched;

  return (
    <div className='my-4'>
      <div data-vjs-player style={{ position: 'relative' }}>
        <video
          ref={videoRef}
          className='video-js vjs-big-play-centered'
          playsInline
          webkit-playsinline
        />
        {isAudioOnly && (
          <div
            // Overlay phủ lên video element. pointerEvents='none' để
            // controls của video.js (play/pause/seekbar) vẫn click được
            // xuyên qua. Bỏ qua thanh control bằng inset bottom > 0.
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: '3em',
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt='Logo trung tâm'
                style={{ maxHeight: '70%', maxWidth: '60%', objectFit: 'contain' }}
              />
            ) : (
              <div className='text-muted'>Bài học chỉ có âm thanh</div>
            )}
          </div>
        )}
      </div>
      <div className='my-1'></div>
      <VideoProgressMapa
        mapa={displayMapa}
        duration={Math.floor(playerRef.current?.duration() || 0)}
        intervalTime={intervalTime}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 4px',
          fontSize: '0.82rem',
          color: '#6b7280',
        }}
      >
        <span>Độ dài: {formatTime(displayMapa.length * intervalTime, 'clock')}</span>
        <span style={{ fontWeight: 600, color: isCompleted ? '#059669' : '#1d4ed8' }}>
          {serverPercent}%
        </span>
        <span>Đã xem: {formatTime(watchedCount * intervalTime, 'clock')}</span>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 8,
          padding: '8px 0',
        }}
      >
        <button
          onClick={() => {
            const player = playerRef.current;
            if (!player) return;
            const newTime = player.currentTime() - intervalTime;
            seekTo(newTime);
          }}
          title={`Tua lại ${intervalTime}s`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '6px 12px',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            background: '#fff',
            color: '#374151',
            fontSize: '0.8rem',
            cursor: 'pointer',
          }}
        >
          <MdReplay size={16} />
          {intervalTime}s
        </button>

        <button
          onClick={() => {
            const player = playerRef.current;
            if (!player) return;
            isPlaying ? player.pause() : player.play();
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '6px 16px',
            border: 'none',
            borderRadius: 8,
            background: isPlaying ? '#ef4444' : '#2563eb',
            color: '#fff',
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.15s',
          }}
        >
          {isPlaying ? <><MdPause size={18} /> Dừng</> : <><MdPlayArrow size={18} /> Phát</>}
        </button>

        {hasUnwatched && (
          <button
            onClick={goToNearestUnwatched}
            title='Đến vị trí chưa xem'
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '6px 12px',
              border: '1px solid #bfdbfe',
              borderRadius: 8,
              background: '#eff6ff',
              color: '#1d4ed8',
              fontSize: '0.8rem',
              cursor: 'pointer',
            }}
          >
            <MdSkipNext size={16} />
            Chưa xem
          </button>
        )}
      </div>

    </div>
  );
};

export default VideoPlayer;
